import React, { useState } from 'react';
import MainLayout from '../../components/ui/MainLayout';
import Icon from '../../components/AppIcon';
import { useNotifications } from '../../contexts/NotificationContext';
import { useRBAC } from '../../contexts/RBACContext';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { logAuditAction } from '../../utils/auditLogger';

const TYPE_STYLES = {
  info: { icon: 'Info', color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-800' },
  success: { icon: 'CheckCircle', color: 'text-green-500', bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200 dark:border-green-800' },
  warning: { icon: 'AlertTriangle', color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200 dark:border-amber-800' },
  error: { icon: 'AlertCircle', color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-800' },
};

const NOTIFICATION_TYPES = ['info', 'success', 'warning', 'error'];

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date?.toLocaleDateString();
}

const EMPTY_NOTIF = { title: '', message: '', type: 'info', target: 'all' };

export default function NotificationCenter() {
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead } = useNotifications();
  const { isAdmin } = useRBAC();
  const { user } = useAuth();
  const [filter, setFilter] = React.useState('all');

  // Admin state
  const [showCompose, setShowCompose] = useState(false);
  const [composeForm, setComposeForm] = useState(EMPTY_NOTIF);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sendSuccess, setSendSuccess] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const filtered = notifications?.filter(n => {
    if (filter === 'unread') return !n?.is_read;
    if (filter === 'read') return n?.is_read;
    return true;
  });

  const stats = {
    total: notifications?.length,
    unread: unreadCount,
    today: notifications?.filter(n => {
      const d = new Date(n.created_at);
      const today = new Date();
      return d?.toDateString() === today?.toDateString();
    })?.length,
  };

  const handleSendNotification = async () => {
    if (!composeForm?.title?.trim() || !composeForm?.message?.trim()) {
      setSendError('Title and message are required.');
      return;
    }
    setSending(true);
    setSendError('');
    try {
      const { data, error } = await supabase?.from('notifications')?.insert({
        title: composeForm?.title,
        message: composeForm?.message,
        type: composeForm?.type,
        user_id: user?.id, // sent by admin; in a real system you'd broadcast to all/specific users
        is_read: false,
      })?.select()?.single();
      if (error) throw error;
      await logAuditAction({ action: 'SEND', module: 'notifications', recordId: data?.id, recordSummary: `Sent notification: "${composeForm?.title}"`, user });
      setSendSuccess('Notification sent successfully.');
      setComposeForm(EMPTY_NOTIF);
      setTimeout(() => { setSendSuccess(''); setShowCompose(false); }, 2000);
    } catch (err) {
      setSendError(err?.message || 'Failed to send notification.');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteNotification = async (notifId) => {
    try {
      const { error } = await supabase?.from('notifications')?.delete()?.eq('id', notifId);
      if (error) throw error;
      await logAuditAction({ action: 'DELETE', module: 'notifications', recordId: notifId, recordSummary: 'Deleted notification', user });
      setDeleteConfirmId(null);
      // Refresh via context would happen automatically via realtime; force reload if needed
      window.location?.reload();
    } catch (err) {
      console.error('Delete notification error:', err);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>Notification Center</h1>
            <p className="text-sm text-muted-foreground mt-1">All your in-app notifications in one place</p>
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <Icon name="CheckCheck" size={14} />
                Mark all as read
              </button>
            )}
            {/* Admin: Send Notification button */}
            {isAdmin && (
              <button
                onClick={() => { setShowCompose(true); setSendError(''); setSendSuccess(''); }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
              >
                <Icon name="Send" size={14} />
                Send Notification
              </button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Total', value: stats?.total, icon: 'Bell', color: 'text-blue-500' },
            { label: 'Unread', value: stats?.unread, icon: 'BellDot', color: 'text-amber-500' },
            { label: 'Today', value: stats?.today, icon: 'CalendarDays', color: 'text-green-500' },
          ]?.map(s => (
            <div key={s?.label} className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name={s?.icon} size={16} className={s?.color} />
                <span className="text-xs text-muted-foreground font-medium">{s?.label}</span>
              </div>
              <div className="text-2xl font-bold text-foreground">{s?.value}</div>
            </div>
          ))}
        </div>

        {/* Filter */}
        <div className="flex gap-1 bg-muted/30 rounded-lg p-1 w-fit">
          {[
            { id: 'all', label: 'All' },
            { id: 'unread', label: `Unread${unreadCount > 0 ? ` (${unreadCount})` : ''}` },
            { id: 'read', label: 'Read' },
          ]?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setFilter(tab?.id)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${filter === tab?.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {tab?.label}
            </button>
          ))}
        </div>

        {/* Notification List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Icon name="Loader2" size={24} className="animate-spin text-muted-foreground" />
          </div>
        ) : filtered?.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Icon name="BellOff" size={40} className="mx-auto mb-3 opacity-30" />
            <p className="font-medium">{filter === 'unread' ? 'No unread notifications' : 'No notifications'}</p>
            <p className="text-sm mt-1">You are all caught up!</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered?.map(notification => {
              const typeStyle = TYPE_STYLES?.[notification?.type] || TYPE_STYLES?.info;
              return (
                <div
                  key={notification?.id}
                  className={`flex items-start gap-4 p-4 rounded-xl border transition-all ${
                    !notification?.is_read
                      ? `${typeStyle?.bg} ${typeStyle?.border}`
                      : 'bg-card border-border'
                  }`}
                >
                  <div
                    onClick={() => !notification?.is_read && markAsRead(notification?.id)}
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 cursor-pointer ${!notification?.is_read ? 'bg-white/50 dark:bg-black/20' : 'bg-muted'}`}
                  >
                    <Icon name={typeStyle?.icon} size={16} className={!notification?.is_read ? typeStyle?.color : 'text-muted-foreground'} />
                  </div>
                  <div
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => !notification?.is_read && markAsRead(notification?.id)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className={`text-sm font-semibold ${!notification?.is_read ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {notification?.title}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {!notification?.is_read && (
                          <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                        )}
                        <span className="text-xs text-muted-foreground whitespace-nowrap">{timeAgo(notification?.created_at)}</span>
                      </div>
                    </div>
                    <p className={`text-sm mt-0.5 ${!notification?.is_read ? 'text-foreground/80' : 'text-muted-foreground'}`}>
                      {notification?.message}
                    </p>
                  </div>
                  {/* Admin delete button */}
                  {isAdmin && (
                    <button
                      onClick={() => setDeleteConfirmId(notification?.id)}
                      title="Delete notification"
                      className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex-shrink-0"
                    >
                      <Icon name="Trash2" size={14} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* Admin Compose Modal */}
      {showCompose && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl w-full max-w-lg">
            <div className="p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold text-foreground">Send Notification</h2>
                <button onClick={() => setShowCompose(false)} className="p-1 rounded-lg hover:bg-muted transition-colors">
                  <Icon name="X" size={18} className="text-muted-foreground" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Type</label>
                  <div className="flex gap-2 flex-wrap">
                    {NOTIFICATION_TYPES?.map(t => {
                      const ts = TYPE_STYLES?.[t];
                      return (
                        <button
                          key={t}
                          onClick={() => setComposeForm(f => ({ ...f, type: t }))}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${composeForm?.type === t ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}
                        >
                          <Icon name={ts?.icon} size={12} className={ts?.color} />
                          {t?.charAt(0)?.toUpperCase() + t?.slice(1)}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Title *</label>
                  <input type="text" value={composeForm?.title} onChange={e => setComposeForm(f => ({ ...f, title: e?.target?.value }))} placeholder="Notification title..." className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Message *</label>
                  <textarea value={composeForm?.message} onChange={e => setComposeForm(f => ({ ...f, message: e?.target?.value }))} rows={3} placeholder="Notification message..." className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" />
                </div>
                {sendError && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 text-sm">
                    <Icon name="AlertCircle" size={14} />
                    {sendError}
                  </div>
                )}
                {sendSuccess && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-600 text-sm">
                    <Icon name="CheckCircle" size={14} />
                    {sendSuccess}
                  </div>
                )}
              </div>
              <div className="flex gap-3 mt-5">
                <button onClick={handleSendNotification} disabled={sending} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50">
                  {sending ? <Icon name="Loader2" size={14} className="animate-spin" /> : <Icon name="Send" size={14} />}
                  {sending ? 'Sending...' : 'Send Now'}
                </button>
                <button onClick={() => setShowCompose(false)} className="px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirm Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Icon name="Trash2" size={18} className="text-red-500" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Delete Notification</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => handleDeleteNotification(deleteConfirmId)} className="flex-1 px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors">Delete</button>
              <button onClick={() => setDeleteConfirmId(null)} className="flex-1 px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
